import { supabase } from '@/lib/supabase';
import { getAdminRealm } from '@/lib/adminAuth';

export type PlanKind = 'feed' | 'stories';
// Two-stage approval workflow statuses:
//   Draft           – created, not yet reviewed (Step 3 pending)
//   Review Approved  – passed Step 3 review, eligible to enter Step 4
//   Rejected         – disapproved in Step 4, sent back to Step 3 for reapproval
//   Approved         – final approval granted in Step 4
//   Scheduled        – approved + scheduled (Step 5)
export type ApprovalStatus = 'Draft' | 'Review Approved' | 'Rejected' | 'Approved' | 'Scheduled';
export type StickerType =
  | 'poll'
  | 'question'
  | 'quiz'
  | 'slider'
  | 'link'
  | 'location'
  | 'music'
  | 'mention'
  | 'countdown';

export interface PlanSticker {
  type: StickerType;
  label: string;
  detail?: string;
}

export interface PlanFrame {
  id: string;
  day_id: string;
  order_index: number;
  image: string;
  headline: string;
  copy: string;
  stickers: PlanSticker[];
  link_destination: string;
  // feed-specific
  caption: string;
  hashtags: string[];
  cta: string;
  post_time: string;
  post_type: string;
  approval_status: ApprovalStatus;
}

export interface PlanDay {
  id: string;
  kind: PlanKind;
  day_label: string;
  date_label: string;
  theme: string;
  narrative: string;
  order_index: number;
  approval_status: ApprovalStatus;
  frames: PlanFrame[];
}

export const APPROVAL_STATUSES: ApprovalStatus[] = [
  'Draft',
  'Review Approved',
  'Rejected',
  'Approved',
  'Scheduled',
];

// ---- Two-stage approval gating helpers ----
// These centralize the workflow rules so the app and the backend planner agree:
//   Step 3 (Review)  must mark a post "Review Approved" before Step 4 can see it.
//   Step 4 (Approve) either grants final "Approved" OR disapproves -> "Rejected",
//                    which sends the post back to Step 3 for reapproval.

/** A post has cleared Step 3 review and may enter Step 4. */
export function isReviewApproved(status: ApprovalStatus): boolean {
  return status === 'Review Approved' || status === 'Approved' || status === 'Scheduled';
}

/** A post was disapproved in Step 4 and is awaiting reapproval in Step 3. */
export function isRejected(status: ApprovalStatus): boolean {
  return status === 'Rejected';
}

/** A post has received final approval in Step 4. */
export function isFinalApproved(status: ApprovalStatus): boolean {
  return status === 'Approved' || status === 'Scheduled';
}

/** Whether Step 4 (Approve) may be entered for this post. */
export function canEnterApproveStep(status: ApprovalStatus): boolean {
  return isReviewApproved(status);
}

/** Mark a frame (and its parent day) as Step-3 review approved. */
export async function setReviewApproved(frameId: string, dayId: string): Promise<void> {
  await updateFrame(frameId, { approval_status: 'Review Approved' });
  await supabase
    .from('content_plan_days')
    .update({ approval_status: 'Review Approved', updated_at: new Date().toISOString() })
    .eq('id', dayId);
}

/** Send a frame back to Step 3 (Draft) — clears any prior review approval. */
export async function sendBackToDraft(frameId: string, dayId: string): Promise<void> {
  await updateFrame(frameId, { approval_status: 'Draft' });
  await supabase
    .from('content_plan_days')
    .update({ approval_status: 'Draft', updated_at: new Date().toISOString() })
    .eq('id', dayId);
}

/** Step 4 final approval. */
export async function setFinalApproved(frameId: string, dayId: string): Promise<void> {
  await updateFrame(frameId, { approval_status: 'Approved' });
  await supabase
    .from('content_plan_days')
    .update({ approval_status: 'Approved', updated_at: new Date().toISOString() })
    .eq('id', dayId);
}

/** Step 4 disapproval — sends the post back to Step 3 for reapproval. */
export async function setRejected(frameId: string, dayId: string): Promise<void> {
  await updateFrame(frameId, { approval_status: 'Rejected' });
  await supabase
    .from('content_plan_days')
    .update({ approval_status: 'Rejected', updated_at: new Date().toISOString() })
    .eq('id', dayId);
}
export const STICKER_TYPES: StickerType[] = [
  'poll',
  'question',
  'quiz',
  'slider',
  'link',
  'location',
  'music',
  'mention',
  'countdown',
];

// ---- Loaders ----

export async function fetchPlan(kind: PlanKind): Promise<PlanDay[]> {
  const { data: days, error } = await supabase
    .from('content_plan_days')
    .select('*')
    .eq('kind', kind)
    .eq('realm', getAdminRealm())
    .order('order_index', { ascending: true });

  if (error || !days || days.length === 0) return [];

  const ids = days.map((d: any) => d.id);
  const { data: frames } = await supabase
    .from('content_plan_frames')
    .select('*')
    .in('day_id', ids)
    .order('order_index', { ascending: true });

  const framesByDay: Record<string, PlanFrame[]> = {};
  (frames || []).forEach((f: any) => {
    if (!framesByDay[f.day_id]) framesByDay[f.day_id] = [];
    framesByDay[f.day_id].push({
      id: f.id,
      day_id: f.day_id,
      order_index: f.order_index,
      image: f.image,
      headline: f.headline,
      copy: f.copy,
      stickers: Array.isArray(f.stickers) ? f.stickers : [],
      link_destination: f.link_destination || '',
      caption: f.caption || '',
      hashtags: Array.isArray(f.hashtags) ? f.hashtags : [],
      cta: f.cta || '',
      post_time: f.post_time || '',
      post_type: f.post_type || 'Feed Post',
      approval_status: (f.approval_status || 'Draft') as ApprovalStatus,
    });
  });

  return days.map((d: any) => ({
    id: d.id,
    kind: d.kind,
    day_label: d.day_label,
    date_label: d.date_label || '',
    theme: d.theme || '',
    narrative: d.narrative || '',
    order_index: d.order_index,
    approval_status: (d.approval_status || 'Draft') as ApprovalStatus,
    frames: framesByDay[d.id] || [],
  }));
}

// ---- Mutators ----

export async function createDay(kind: PlanKind, order_index: number): Promise<string | null> {
  const { data, error } = await supabase
    .from('content_plan_days')
    .insert({
      kind,
      day_label: 'New Day',
      date_label: '',
      theme: '',
      narrative: '',
      order_index,
      approval_status: 'Draft',
      realm: getAdminRealm(),
    })
    .select('id')
    .single();
  if (error || !data) return null;
  return data.id as string;
}

export async function updateDay(id: string, patch: Partial<PlanDay>): Promise<void> {
  const allowed: any = {};
  ['day_label', 'date_label', 'theme', 'narrative', 'approval_status', 'order_index'].forEach((k) => {
    if (k in patch) allowed[k] = (patch as any)[k];
  });
  allowed.updated_at = new Date().toISOString();
  await supabase.from('content_plan_days').update(allowed).eq('id', id);
}

export async function deleteDay(id: string): Promise<void> {
  await supabase.from('content_plan_days').delete().eq('id', id);
}

export async function createFrame(day_id: string, order_index: number, kind: PlanKind): Promise<string | null> {
  const base: any = {
    day_id,
    order_index,
    image: '',
    headline: '',
    copy: '',
    stickers: [],
    link_destination: '',
    caption: '',
    hashtags: [],
    cta: '',
    post_time: kind === 'feed' ? '9:00 AM' : '',
    post_type: kind === 'feed' ? 'Feed Post' : 'Story',
    approval_status: 'Draft',
    realm: getAdminRealm(),
  };
  const { data, error } = await supabase
    .from('content_plan_frames')
    .insert(base)
    .select('id')
    .single();
  if (error || !data) return null;
  return data.id as string;
}

export async function updateFrame(id: string, patch: Partial<PlanFrame>): Promise<void> {
  const allowed: any = {};
  [
    'image',
    'headline',
    'copy',
    'stickers',
    'link_destination',
    'caption',
    'hashtags',
    'cta',
    'post_time',
    'post_type',
    'approval_status',
    'order_index',
  ].forEach((k) => {
    if (k in patch) allowed[k] = (patch as any)[k];
  });
  allowed.updated_at = new Date().toISOString();
  await supabase.from('content_plan_frames').update(allowed).eq('id', id);
}

export async function deleteFrame(id: string): Promise<void> {
  await supabase.from('content_plan_frames').delete().eq('id', id);
}

export async function reorderFrames(day_id: string, ordered_ids: string[]): Promise<void> {
  // Update each frame's order_index sequentially
  await Promise.all(
    ordered_ids.map((id, idx) =>
      supabase.from('content_plan_frames').update({ order_index: idx }).eq('id', id),
    ),
  );
}

export async function reorderDays(kind: PlanKind, ordered_ids: string[]): Promise<void> {
  await Promise.all(
    ordered_ids.map((id, idx) =>
      supabase.from('content_plan_days').update({ order_index: idx }).eq('id', id),
    ),
  );
}

// ---- Image upload ----

export async function uploadPlannerImage(file: File): Promise<string | null> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('content-planner').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) return null;
  const { data } = supabase.storage.from('content-planner').getPublicUrl(path);
  return data.publicUrl;
}

// ---- Default seed (matches the static plan currently shipping on /instagram-content) ----

export const SEED_FEED: Omit<PlanDay, 'id'>[] = [
  {
    kind: 'feed',
    day_label: 'Monday',
    date_label: 'Week 1 · Day 1',
    theme: 'Aspirational Opener',
    narrative: '',
    order_index: 0,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900188372_819697da.jpg',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          "Some mornings the Ionian feels less like a sea and more like a secret.\n\nThis is where every Zera journey begins — turquoise water, sun-warmed teak, and a coastline most of the world hasn't discovered yet.\n\nWhere should we drop anchor next?",
        hashtags: ['#ZeraTravel', '#AlbanianRiviera', '#LuxuryYachtCharter', '#HiddenMediterranean', '#TravelInStyle'],
        cta: 'Tap the link in bio to plan your private charter.',
        post_time: '9:00 AM',
        post_type: 'Feed Post',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Tuesday',
    date_label: 'Week 1 · Day 2',
    theme: 'Villa Showcase',
    narrative: '',
    order_index: 1,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900206532_bc1c9e7b.jpg',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          "A terrace. An olive grove. An entire coastline to yourself.\n\nOur curated villas aren't just stays — they're the setting for the kind of evenings you remember for years. Long dinners. Quiet swims. The slow honey light of an Albanian sunset.\n\nSwipe to step inside →",
        hashtags: ['#PrivateVilla', '#AlbanianVilla', '#LuxuryStays', '#ZeraVillas', '#MediterraneanLife', '#SlowTravel'],
        cta: 'Reserve yours — link in bio.',
        post_time: '12:30 PM',
        post_type: 'Carousel',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Wednesday',
    date_label: 'Week 1 · Day 3',
    theme: 'Cultural Heritage',
    narrative: '',
    order_index: 2,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900230017_1f4f6719.png',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          'Berat is called the City of a Thousand Windows — and at blue hour, every one of them tells a story.\n\nOttoman stone. Byzantine churches. UNESCO-protected streets where time moves at the pace of a slow espresso.\n\nThis is the Albania our travelers fall in love with. The one beyond the postcards.',
        hashtags: ['#BeratAlbania', '#UNESCO', '#CulturalTravel', '#HiddenEurope', '#ZeraJourneys', '#HistoricEurope'],
        cta: 'Read our Berat guide — link in bio.',
        post_time: '6:00 PM',
        post_type: 'Reel',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Thursday',
    date_label: 'Week 1 · Day 4',
    theme: 'Culinary Story',
    narrative: '',
    order_index: 3,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900255094_23de8646.png',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          "Lunch, the Albanian way.\n\nFish pulled from the Ionian at dawn. Olive oil pressed three villages over. A glass of Kallmet that tastes like the hills it came from.\n\nWe build journeys around tables like this — because the real souvenir is the meal you'll keep talking about.",
        hashtags: ['#AlbanianCuisine', '#FoodieTravel', '#MediterraneanDiet', '#CulinaryJourney', '#ZeraTable', '#FarmToCoast'],
        cta: 'Ask us about our private chef experiences.',
        post_time: '1:00 PM',
        post_type: 'Feed Post',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Friday',
    date_label: 'Week 1 · Day 5',
    theme: 'Adventure & Nature',
    narrative: '',
    order_index: 4,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900274375_d7ea979e.png',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          'Up here, above the cloud line in Theth, you understand why locals call the Albanian Alps the Accursed Mountains — too beautiful to belong to anyone.\n\nFor the traveler who wants more than a beach chair, this is the other Albania. Wild. Quiet. Yours for a morning.\n\nReady to hike it with us?',
        hashtags: ['#TheAlbanianAlps', '#TheGuide', '#AdventureTravel', '#HikingEurope', '#ZeraExpeditions', '#WildEurope'],
        cta: 'Build your custom itinerary — link in bio.',
        post_time: '8:00 AM',
        post_type: 'Reel',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Saturday',
    date_label: 'Week 1 · Day 6',
    theme: 'Heritage Deep Dive',
    narrative: '',
    order_index: 5,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900296496_29e9bfa3.png',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          "2,500 years of empires standing in a single afternoon's walk.\n\nButrint isn't just ruins — it's Greek, Roman, Byzantine, Venetian, and Ottoman, layered like pages in a book the Mediterranean forgot to publish.\n\nOur private guides bring it back to life. Save this post for your next planning session.",
        hashtags: ['#ButrintAlbania', '#UNESCOHeritage', '#AncientHistory', '#ArchaeologyTravel', '#ZeraTravel', '#HistoricalJourneys'],
        cta: 'Book a private heritage tour — link in bio.',
        post_time: '11:00 AM',
        post_type: 'Carousel',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'feed',
    day_label: 'Sunday',
    date_label: 'Week 1 · Day 7',
    theme: 'Lifestyle & Aspiration',
    narrative: '',
    order_index: 6,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900313956_ee63e4c0.jpg',
        headline: '',
        copy: '',
        stickers: [],
        link_destination: '',
        caption:
          "Sunday in Gjirokastër.\n\nNo agenda. No notifications. Just stone streets, a slow walk, and the quiet luxury of having nowhere else to be.\n\nThis is what we mean when we say we don't sell trips — we curate slower lives, one week at a time.\n\nWhere will yours unfold?",
        hashtags: ['#Gjirokaster', '#SlowTravel', '#LuxuryLifestyle', '#ZeraMoments', '#TravelInspiration', '#QuietLuxury'],
        cta: "DM us 'JOURNEY' to start planning.",
        post_time: '5:30 PM',
        post_type: 'Feed Post',
        approval_status: 'Approved',
      },
    ],
  },
];

export const SEED_STORIES: Omit<PlanDay, 'id'>[] = [
  {
    kind: 'stories',
    day_label: 'Monday',
    date_label: 'Week 1 · Day 1',
    theme: 'Anchor Drop — Riviera Morning',
    narrative:
      'Soft-launch the week with a slow-build POV: dawn on deck, anchor down, the kind of swim only guests get. Ends with a poll to learn what the audience wants more of.',
    order_index: 0,
    approval_status: 'Approved',
    frames: [
      {
        id: '',
        day_id: '',
        order_index: 0,
        image: 'https://images.unsplash.com/photo-1599666433232-95036179624a?auto=format&fit=crop&w=1080&q=85',
        headline: 'good morning, ionian',
        copy: '6:42 AM · Sazan Island\nThe sea is still asleep.',
        stickers: [
          { type: 'location', label: 'Sazan Island, Albania' },
          { type: 'music', label: 'Lofi — "Slow Coast" by Mell-ø' },
        ],
        link_destination: '',
        caption: '',
        hashtags: [],
        cta: '',
        post_time: '',
        post_type: 'Story',
        approval_status: 'Approved',
      },
      {
        id: '',
        day_id: '',
        order_index: 1,
        image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1080&q=85',
        headline: 'anchor down',
        copy: "Today's only agenda:\nswim → breakfast → repeat",
        stickers: [{ type: 'poll', label: 'This or That:', detail: 'Riviera / Alps' }],
        link_destination: '',
        caption: '',
        hashtags: [],
        cta: '',
        post_time: '',
        post_type: 'Story',
        approval_status: 'Approved',
      },
      {
        id: '',
        day_id: '',
        order_index: 2,
        image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=1080&q=85',
        headline: 'private cove No. 4',
        copy: 'Most of our charters stop here.\nNone of the cruise ships can.',
        stickers: [
          { type: 'link', label: 'See Yacht Charters', detail: '/yacht-charters' },
          { type: 'mention', label: '@zeratravel' },
        ],
        link_destination: '/yacht-charters',
        caption: '',
        hashtags: [],
        cta: '',
        post_time: '',
        post_type: 'Story',
        approval_status: 'Approved',
      },
      {
        id: '',
        day_id: '',
        order_index: 3,
        image: 'https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?auto=format&fit=crop&w=1080&q=85',
        headline: 'help us plan the week',
        copy: 'Which thread should we pull on next?',
        stickers: [
          {
            type: 'poll',
            label: 'What do you want to see more of?',
            detail: 'Villas / Yachts / Food / Mountains',
          },
        ],
        link_destination: '',
        caption: '',
        hashtags: [],
        cta: '',
        post_time: '',
        post_type: 'Story',
        approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Tuesday',
    date_label: 'Week 1 · Day 2',
    theme: 'Inside the Villa',
    narrative:
      'A slow architectural tour of one of our signature villas — Cap Rodon. Use the slider sticker to gauge "dream factor" and finish with a link sticker straight to the villa page.',
    order_index: 1,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&w=1080&q=85',
        headline: 'meet villa cap rodon',
        copy: 'Six bedrooms.\nOne olive grove.\nZero neighbors.',
        stickers: [{ type: 'location', label: 'Cap Rodon, Albania' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1080&q=85',
        headline: 'the terrace',
        copy: 'Where dinners go long\nand the wifi goes quiet.',
        stickers: [{ type: 'slider', label: 'Dream factor:', detail: 'emoji slider' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1080&q=85',
        headline: 'available aug 14–21',
        copy: 'One window left this summer.\nTap to claim it.',
        stickers: [
          { type: 'link', label: 'Reserve Villa Cap Rodon', detail: '/villa-rentals' },
          { type: 'countdown', label: 'Booking closes Friday 8pm' },
        ],
        link_destination: '/villa-rentals',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Wednesday',
    date_label: 'Week 1 · Day 3',
    theme: 'Berat at Blue Hour',
    narrative:
      'Behind-the-scenes of our Reel shoot. Show the city waking up, the gear, the local guide. Use a question sticker to crowdsource the next city to feature.',
    order_index: 2,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=1080&q=85',
        headline: 'today, we filmed berat',
        copy: 'The city of a thousand windows.\nAll of them watching back.',
        stickers: [
          { type: 'location', label: 'Berat, Albania' },
          { type: 'music', label: 'Albanian iso-polyphony (UNESCO archive)' },
        ],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1542856391-010fb87dcfed?auto=format&fit=crop&w=1080&q=85',
        headline: 'blue hour, 7:48pm',
        copy: 'For 14 minutes a day,\nBerat turns into a painting.',
        stickers: [{ type: 'quiz', label: 'How old is Berat Castle?', detail: '800 · 1,400 · 2,400 yrs' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1601581875039-e899893d520c?auto=format&fit=crop&w=1080&q=85',
        headline: 'where to next?',
        copy: 'Help us pick the next city we feature.',
        stickers: [
          { type: 'question', label: 'Drop a city we should film next:' },
          { type: 'link', label: 'Read our Berat guide', detail: '/blog' },
        ],
        link_destination: '/blog',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Thursday',
    date_label: 'Week 1 · Day 4',
    theme: 'Table for Twelve',
    narrative:
      'Foodie-focused day. Walk the audience from market → kitchen → table. End with a CTA to book the private chef experience.',
    order_index: 3,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?auto=format&fit=crop&w=1080&q=85',
        headline: 'market run, 7am',
        copy: 'Figs, sea bass, hand-pressed olive oil.\nThe menu writes itself.',
        stickers: [{ type: 'location', label: 'Pazari i Ri, Tirana' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1080&q=85',
        headline: 'chef anila plates up',
        copy: 'Tave kosi.\nAlbanian comfort food, refined.',
        stickers: [{ type: 'poll', label: 'Tave kosi or fresh fish?', detail: 'Lamb-yogurt / Sea bass' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1080&q=85',
        headline: 'book the chef',
        copy: 'Private dinners, every itinerary.\nAdd-on starts at €180/guest.',
        stickers: [{ type: 'link', label: 'Add a Chef Experience', detail: '/booking' }],
        link_destination: '/booking',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Friday',
    date_label: 'Week 1 · Day 5',
    theme: 'Into the Alps',
    narrative:
      'Pivot to adventure. Use this day to widen brand perception — Zera is not just beach. Use a countdown for the autumn hiking season opening.',
    order_index: 4,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1080&q=85',
        headline: '6 hours from the coast',
        copy: 'Same country.\nCompletely different planet.',
        stickers: [{ type: 'location', label: 'Theth National Park' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1080&q=85',
        headline: 'the valbona pass',
        copy: 'Three hours up.\nOne lifetime of view.',
        stickers: [
          { type: 'poll', label: 'Riviera vs Alps?', detail: 'Beach / Peak' },
          { type: 'mention', label: '@guesthouse.nik' },
        ],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=1080&q=85',
        headline: 'autumn season opens',
        copy: 'Golden larches. Cold streams.\nFour bookings left.',
        stickers: [
          { type: 'countdown', label: 'Hiking season opens Sept 1' },
          { type: 'link', label: 'Plan an Alpine Itinerary', detail: '/corporate-travel' },
        ],
        link_destination: '/corporate-travel',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Saturday',
    date_label: 'Week 1 · Day 6',
    theme: 'Butrint — A Walking Story',
    narrative:
      'Saturday is for slow scrollers. A mini-documentary in 5 frames. End with a save-this prompt and a link to the heritage tour.',
    order_index: 5,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1555992336-fb0d29498b13?auto=format&fit=crop&w=1080&q=85',
        headline: '2,500 years in one walk',
        copy: 'Greek. Roman. Byzantine. Venetian. Ottoman.\nButrint kept them all.',
        stickers: [{ type: 'location', label: 'Butrint National Park' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?auto=format&fit=crop&w=1080&q=85',
        headline: 'the amphitheater',
        copy: 'Still acoustically perfect.\nWe whispered. It carried.',
        stickers: [{ type: 'music', label: '"Adagio in G Minor" — Albinoni' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1568849676085-51415703900f?auto=format&fit=crop&w=1080&q=85',
        headline: 'the lion gate',
        copy: 'Carved 2,400 years ago.\nStill standing watch.',
        stickers: [{ type: 'quiz', label: 'Which empire built it?', detail: 'Greek · Roman · Byzantine' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 3,
        image: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=1080&q=85',
        headline: 'save this for later',
        copy: 'Then come walk it with us.',
        stickers: [
          { type: 'link', label: 'Book the Heritage Tour', detail: '/booking' },
          { type: 'question', label: 'Ask us anything about Butrint:' },
        ],
        link_destination: '/booking',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
  {
    kind: 'stories',
    day_label: 'Sunday',
    date_label: 'Week 1 · Day 7',
    theme: 'Sunday Slow — Gjirokastër',
    narrative:
      'Close the week soft. No CTA pressure. Repost guest UGC, share a poem, leave the DMs open. Builds parasocial trust.',
    order_index: 6,
    approval_status: 'Approved',
    frames: [
      {
        id: '', day_id: '', order_index: 0,
        image: 'https://images.unsplash.com/photo-1604357209793-fca5dca89f97?auto=format&fit=crop&w=1080&q=85',
        headline: 'sunday in gjirokastër',
        copy: 'Stone city.\nSlow morning.\nNo plans.',
        stickers: [{ type: 'location', label: 'Gjirokastër, Albania' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 1,
        image: 'https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1080&q=85',
        headline: 'a guest sent this in',
        copy: '"We came for a week.\nWe left wanting a different life."\n— Margot & James, July',
        stickers: [{ type: 'mention', label: '@margotandjames (reposted with permission)' }],
        link_destination: '', caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
      {
        id: '', day_id: '', order_index: 2,
        image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1080&q=85',
        headline: 'see you monday',
        copy: 'DMs are open all week.\nWe answer every one.',
        stickers: [
          { type: 'question', label: 'Tell us where you want to go next year:' },
          { type: 'link', label: 'Plan a 2026 Journey', detail: '/' },
        ],
        link_destination: '/',
        caption: '', hashtags: [], cta: '', post_time: '', post_type: 'Story', approval_status: 'Approved',
      },
    ],
  },
];



export async function seedDefaultPlan(): Promise<void> {
  const allSeed = [...SEED_FEED, ...SEED_STORIES];
  for (const day of allSeed) {
    const { data, error } = await supabase
      .from('content_plan_days')
      .insert({
        kind: day.kind,
        day_label: day.day_label,
        date_label: day.date_label,
        theme: day.theme,
        narrative: day.narrative,
        order_index: day.order_index,
        approval_status: day.approval_status,
        realm: getAdminRealm(),
      })
      .select('id')
      .single();
    if (error || !data) continue;
    const dayId = data.id as string;
    if (day.frames.length) {
      await supabase.from('content_plan_frames').insert(
        day.frames.map((f) => ({
          day_id: dayId,
          order_index: f.order_index,
          image: f.image,
          headline: f.headline,
          copy: f.copy,
          stickers: f.stickers,
          link_destination: f.link_destination,
          caption: f.caption,
          hashtags: f.hashtags,
          cta: f.cta,
          post_time: f.post_time,
          post_type: f.post_type,
          approval_status: f.approval_status,
          realm: getAdminRealm(),
        })),
      );
    }
  }
}
